/**
 * The plan's objects as shapes: where a placed radiator or outdoor unit is,
 * which way it faces, the rectangle it covers, and which side of each scanned
 * wall is its room.
 *
 * Moved here from the portal (`apps/portal/src/scan/plan-objects.ts`, which now
 * re-exports these) so the fit checks and the MCS design check can run in the
 * API — `GET /api/surveys/:id/design-check` — and in the installer pack as well
 * as on the drawing, from one copy of the arithmetic.
 *
 * All in plan metres, on the scan's own frame: x = world X, y = world Z, which
 * is screen-down (see `@sts/geometry`'s scan-plan.ts). A rotation is degrees
 * CLOCKWISE on screen, the direction SVG's `rotate()` turns with y pointing
 * down. At 0° an object's length runs along +x and its depth along +y; its
 * local axes are
 *
 *   u = ( cos θ, sin θ)   along the length
 *   v = (−sin θ, cos θ)   across the depth — the FRONT of an outdoor unit
 */

import {
  radiatorLabel,
  type HeatPumpSpec,
  type PlacedGeometry,
  type PlanObjectPoint,
  type PlanObjectShape,
} from "@sts/rules-data";

type Point = PlanObjectPoint;

/** A plan object as anything that checks the plan needs it: what it is, where, and on which storey. */
export type PlanObjectOnPlan = PlanObjectShape & {
  readonly id: string;
  readonly storey: number;
};

const DEG = Math.PI / 180;

/** Any angle, as the one value in [0, 360) that means the same thing. */
export function normaliseDegrees(deg: number): number {
  const d = deg % 360;
  const n = d < 0 ? d + 360 : d;
  return n >= 360 - 1e-9 || Math.abs(n) < 1e-9 ? 0 : n;
}

export function axesOf(rotationDeg: number): { u: Point; v: Point } {
  const c = Math.cos(rotationDeg * DEG);
  const s = Math.sin(rotationDeg * DEG);
  return { u: { x: c, y: s }, v: { x: -s, y: c } };
}

/**
 * The four corners of a placed rectangle — length along u, depth along v —
 * in order back-left, back-right, front-right, front-left (back = −v).
 */
export function placedCorners(
  geometry: PlacedGeometry,
  lengthM: number,
  depthM: number,
): readonly Point[] {
  const { u, v } = axesOf(geometry.rotationDeg);
  const hl = lengthM / 2;
  const hd = depthM / 2;
  const at = (su: number, sv: number): Point => ({
    x: geometry.x + u.x * hl * su + v.x * hd * sv,
    y: geometry.y + u.y * hl * su + v.y * hd * sv,
  });
  return [at(-1, -1), at(1, -1), at(1, 1), at(-1, 1)];
}

/** The plan footprint of a placed object, metres along u and along v. */
export function footprintM(
  object: PlanObjectShape,
): { lengthM: number; depthM: number } | undefined {
  if (object.kind === "heat-pump") {
    return { lengthM: object.spec.widthMm / 1000, depthM: object.spec.depthMm / 1000 };
  }
  if (object.kind === "radiator") {
    return { lengthM: object.spec.lengthMm / 1000, depthM: object.spec.depthMm / 1000 };
  }
  return undefined;
}

// ---------------------------------------------------------------- walls ----

/** A wall's run on the plan, and which side of it is the room. */
export interface PlanWall {
  readonly id: string;
  readonly start: Point;
  readonly end: Point;
  /** Unit normal from the wall line into its own room. */
  readonly inward: Point;
}

/**
 * Walls with their inward side resolved from the room's centre — the same test
 * the plan's door marks use to swing a door into the room it belongs to.
 */
export function wallsFacingRooms(
  rooms: readonly {
    readonly centre: Point;
    readonly segments: readonly {
      readonly id: string;
      readonly kind: string;
      readonly start: Point;
      readonly end: Point;
    }[];
  }[],
): readonly PlanWall[] {
  const walls: PlanWall[] = [];
  for (const room of rooms) {
    for (const segment of room.segments) {
      if (segment.kind !== "wall") continue;
      const dx = segment.end.x - segment.start.x;
      const dy = segment.end.y - segment.start.y;
      const length = Math.hypot(dx, dy);
      if (!(length > 1e-6)) continue;
      const n = { x: -dy / length, y: dx / length };
      const mid = { x: (segment.start.x + segment.end.x) / 2, y: (segment.start.y + segment.end.y) / 2 };
      const toCentre = { x: room.centre.x - mid.x, y: room.centre.y - mid.y };
      const inward = n.x * toCentre.x + n.y * toCentre.y >= 0 ? n : { x: -n.x, y: -n.y };
      walls.push({ id: segment.id, start: segment.start, end: segment.end, inward });
    }
  }
  return walls;
}

// ------------------------------------------------------------- captions ----

const mm = (n: number): string => `${Math.round(n)}`;

/** "Vaillant aroTHERM plus 7 kW · 1100 × 450 mm" — the name and the real footprint. */
export function heatPumpCaption(spec: Pick<HeatPumpSpec, "name" | "widthMm" | "depthMm">): string {
  return `${spec.name} · ${mm(spec.widthMm)} × ${mm(spec.depthMm)} mm`;
}

/** What each kind is called on the plan and in messages about it. */
export const PLAN_OBJECT_KIND_NAMES: Readonly<Record<PlanObjectShape["kind"], string>> = {
  "heat-pump": "Heat pump",
  radiator: "Radiator",
  pipe: "Pipe",
  fitting: "Fitting",
  cylinder: "Cylinder",
  buffer: "Buffer",
};

/** One line naming a radiator or outdoor unit — "K2 600×1200", or the unit's caption. */
export function footprintedCaption(
  object: Extract<PlanObjectShape, { kind: "radiator" | "heat-pump" }>,
): string {
  return object.kind === "heat-pump" ? heatPumpCaption(object.spec) : radiatorLabel(object.spec);
}
